import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import VoiceRecorder from "@/components/VoiceRecorder";
import {
  Megaphone, Send, Paperclip, Users, Building2, Briefcase, UserCircle,
  Trash2, X, ExternalLink, MessageSquareText, CheckCircle2, Eye, Loader2,
  History,
} from "lucide-react";

/**
 * Phase 5M — Broadcast Hub (Admin)
 * Compose + History
 */
export default function Broadcasts() {
  const [tab, setTab] = useState("compose");
  return (
    <div className="space-y-4" data-testid="broadcasts-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Megaphone className="h-3 w-3" /> Phase 5M · Broadcast Messaging
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Broadcasts
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">
          Send announcements to employees via Portal &amp; WhatsApp click-to-send links. Attach files or voice notes.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-red-50 border border-red-200">
          <TabsTrigger value="compose" data-testid="tab-compose"><Send className="h-3.5 w-3.5 mr-1" />Compose</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history"><History className="h-3.5 w-3.5 mr-1" />History</TabsTrigger>
        </TabsList>
        <TabsContent value="compose" className="mt-4"><ComposeTab onSent={() => setTab("history")} /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ComposeTab({ onSent }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [voiceUrl, setVoiceUrl] = useState(null);
  const [sendWhatsapp, setSendWhatsapp] = useState(true);
  const [recipients, setRecipients] = useState({
    employee_ids: [], unit_ids: [], department_ids: [],
    categories: [], roles: [], all_active: false,
  });
  const [previewCount, setPreviewCount] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [recDialog, setRecDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [createdBid, setCreatedBid] = useState(null);

  useEffect(() => {
    api.get("/employees").then(r => setEmployees(r.data || []));
    api.get("/units").then(r => setUnits(r.data || [])).catch(() => {});
    api.get("/departments").then(r => setDepartments(r.data || [])).catch(() => {});
  }, []);

  // Live recipient count preview from selected filters
  useEffect(() => {
    if (!employees.length) { setPreviewCount(0); return; }
    if (recipients.all_active) {
      setPreviewCount(employees.filter(e => e.status === "Working").length);
      return;
    }
    const set = new Set();
    employees.forEach(e => {
      if (e.status !== "Working") return;
      if (recipients.employee_ids.includes(e.id)) set.add(e.id);
      if (recipients.unit_ids.includes(e.unit_id)) set.add(e.id);
      if (recipients.department_ids.includes(e.department_id)) set.add(e.id);
      if (recipients.categories.includes(e.category)) set.add(e.id);
      if (recipients.roles.includes(e.role)) set.add(e.id);
    });
    setPreviewCount(set.size);
  }, [recipients, employees]);

  const onFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/broadcasts/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments([...attachments, data]);
      toast.success(`Attached ${data.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    }
  };

  const removeAttachment = (idx) => setAttachments(attachments.filter((_, i) => i !== idx));

  const send = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    if (!message.trim() || message.trim().length < 2) { toast.error("Message required"); return; }
    if (previewCount === 0) { toast.error("Pick at least one recipient"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/broadcasts", {
        title: title.trim(),
        message: message.trim(),
        recipients,
        attachments,
        voice_url: voiceUrl,
        send_whatsapp: sendWhatsapp,
      });
      toast.success(`Broadcast sent to ${data.recipient_count} employees ✓`);
      setCreatedBid(data.id);
      // Reset
      setTitle(""); setMessage(""); setAttachments([]); setVoiceUrl(null);
      setRecipients({ employee_ids: [], unit_ids: [], department_ids: [], categories: [], roles: [], all_active: false });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="compose-tab">
      {/* LEFT — Compose */}
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g. Holiday on 26th January"
                    maxLength={160}
                    data-testid="broadcast-title" />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                       rows={6} placeholder="Type your announcement here…"
                       maxLength={4000}
                       data-testid="broadcast-message" />
            <p className="text-[10px] text-slate-400 mt-1">{message.length}/4000</p>
          </div>

          {/* Attachments */}
          <div>
            <Label className="text-xs flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" />Attachments (max 25 MB each)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="file" id="bc-file" className="hidden"
                      onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
              <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("bc-file").click()} data-testid="add-attachment">
                <Paperclip className="h-3.5 w-3.5 mr-1" />Add File
              </Button>
              <span className="text-[10px] text-slate-400">PDF, image, doc, voice</span>
            </div>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-1">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`attachment-${i}`}>
                    <Paperclip className="h-3 w-3" />
                    <span className="flex-1 truncate">{a.name}</span>
                    <span className="text-[10px] text-slate-400">{Math.round(a.size / 1024)} KB</span>
                    <button onClick={() => removeAttachment(i)} className="text-red-600 hover:text-red-800"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Voice */}
          <div>
            <Label className="text-xs">Voice Note (optional, 60s max)</Label>
            <VoiceRecorder value={voiceUrl} onChange={(url) => setVoiceUrl(url)}
                            testIdPrefix="bc-voice" compact maxSeconds={60} />
          </div>

          {/* Send via WhatsApp toggle */}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Checkbox id="wa-toggle" checked={sendWhatsapp} onCheckedChange={setSendWhatsapp} data-testid="wa-toggle" />
            <Label htmlFor="wa-toggle" className="text-xs cursor-pointer flex items-center gap-1">
              <MessageSquareText className="h-3.5 w-3.5 text-emerald-600" />
              Generate WhatsApp click-to-send links (you'll click each to send)
            </Label>
          </div>
        </div>

        <Button onClick={send} disabled={sending || previewCount === 0} className="brand-bg brand-bg-hover text-white w-full" data-testid="send-broadcast">
          {sending ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Sending…</> : <><Send className="h-4 w-4 mr-1" />Broadcast to {previewCount} employees</>}
        </Button>

        {createdBid && (
          <div className="bg-emerald-50 border border-emerald-300 rounded p-3 space-y-2" data-testid="post-send-actions">
            <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
              <CheckCircle2 className="h-4 w-4" />Broadcast sent successfully
            </div>
            <p className="text-xs text-emerald-800">Open WhatsApp click-to-send links to message recipients directly:</p>
            <Button variant="outline" size="sm" onClick={() => window.open(`/admin/broadcasts/${createdBid}`, "_blank")} data-testid="open-wa-panel">
              <ExternalLink className="h-3.5 w-3.5 mr-1" />Open WhatsApp Links Panel
            </Button>
          </div>
        )}
      </div>

      {/* RIGHT — Recipients */}
      <div className="space-y-3">
        <div className="bg-white border border-red-200 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-bold flex items-center gap-1.5"><Users className="h-4 w-4 text-red-700" />Recipients</h3>
          <div className="bg-red-50 rounded p-3 text-center">
            <div className="text-3xl font-bold text-red-700" data-testid="recipient-preview-count">{previewCount}</div>
            <div className="text-[10px] uppercase tracking-wider text-red-900">employees selected</div>
          </div>

          {/* All Active toggle */}
          <div className="flex items-center gap-2 border-b pb-2">
            <Checkbox id="all-active" checked={recipients.all_active}
                       onCheckedChange={(v) => setRecipients({ ...recipients, all_active: !!v })}
                       data-testid="all-active-toggle" />
            <Label htmlFor="all-active" className="text-xs cursor-pointer">All Working Employees</Label>
          </div>

          {!recipients.all_active && (
            <>
              <MultiPickerSection
                icon={Building2} label="Units" items={units}
                selected={recipients.unit_ids}
                onChange={(ids) => setRecipients({ ...recipients, unit_ids: ids })}
                testidPrefix="unit"
              />
              <MultiPickerSection
                icon={Briefcase} label="Departments" items={departments}
                selected={recipients.department_ids}
                onChange={(ids) => setRecipients({ ...recipients, department_ids: ids })}
                testidPrefix="dept"
              />
              <MultiPickerSection
                icon={UserCircle} label="Categories"
                items={[
                  { id: "white_collar", name: "White Collar" },
                  { id: "white_collar_female", name: "White Collar (Female)" },
                  { id: "blue_collar", name: "Blue Collar" },
                  { id: "driver", name: "Driver" },
                  { id: "manual_entry", name: "Manual Entry" },
                ]}
                selected={recipients.categories}
                onChange={(ids) => setRecipients({ ...recipients, categories: ids })}
                testidPrefix="cat"
              />
              <MultiPickerSection
                icon={UserCircle} label="Roles"
                items={[
                  { id: "employee", name: "Employee" },
                  { id: "manager", name: "Manager" },
                  { id: "hod", name: "HOD" },
                  { id: "senior_management", name: "Senior Mgmt" },
                  { id: "ea", name: "EA" },
                ]}
                selected={recipients.roles}
                onChange={(ids) => setRecipients({ ...recipients, roles: ids })}
                testidPrefix="role"
              />
              <Button variant="outline" size="sm" onClick={() => setRecDialog(true)} className="w-full" data-testid="pick-individuals">
                <UserCircle className="h-3.5 w-3.5 mr-1" />Pick Individual Employees ({recipients.employee_ids.length})
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Individual employee picker dialog */}
      <EmployeePickerDialog
        open={recDialog}
        onClose={() => setRecDialog(false)}
        employees={employees}
        selected={recipients.employee_ids}
        onChange={(ids) => setRecipients({ ...recipients, employee_ids: ids })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
function MultiPickerSection({ icon: Icon, label, items, selected, onChange, testidPrefix }) {
  if (!items?.length) return null;
  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <div>
      <Label className="text-[11px] flex items-center gap-1 text-slate-700"><Icon className="h-3 w-3" />{label}</Label>
      <div className="flex flex-wrap gap-1 mt-1">
        {items.map(it => {
          const on = selected.includes(it.id);
          return (
            <button key={it.id} onClick={() => toggle(it.id)}
                     className={`text-[10px] px-2 py-0.5 rounded border ${on ? "bg-red-600 border-red-700 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}
                     data-testid={`${testidPrefix}-${it.id}`}>
              {it.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function EmployeePickerDialog({ open, onClose, employees, selected, onChange }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return employees.filter(e => e.status === "Working" && (!s ||
      (e.name || "").toLowerCase().includes(s) ||
      (e.employee_code || "").toLowerCase().includes(s)));
  }, [employees, q]);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle>Pick Individual Employees</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code…" data-testid="emp-search" />
          <Button variant="outline" size="sm" onClick={() => onChange([])} data-testid="emp-clear">Clear ({selected.length})</Button>
        </div>
        <div className="overflow-y-auto flex-1 border rounded">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-slate-100">
              {filtered.slice(0, 500).map(e => (
                <tr key={e.id} className="hover:bg-red-50/30">
                  <td className="px-2 py-1.5 w-8"><Checkbox checked={selected.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`emp-pick-${e.id}`} /></td>
                  <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{e.employee_code}</td>
                  <td className="px-2 py-1.5">{e.name}</td>
                  <td className="px-2 py-1.5 text-[10px] text-slate-500">{e.department_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center text-xs text-slate-400 py-6">No employees match search</p>}
          {filtered.length > 500 && <p className="text-center text-[10px] text-slate-400 py-2">Showing first 500 — refine search</p>}
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="brand-bg brand-bg-hover text-white">Done ({selected.length} selected)</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function HistoryTab() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/broadcasts");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="history-tab">
      {items.length === 0 && (
        <div className="text-center text-xs text-slate-400 py-12 border border-dashed rounded">
          No broadcasts yet. Send your first announcement from Compose tab.
        </div>
      )}
      {items.map(b => (
        <div key={b.id} className="bg-white border border-slate-200 rounded p-4" data-testid={`broadcast-row-${b.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-slate-900">{b.title}</h3>
              <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{b.message}</p>
              <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                <span>By <b>{b.sender_name}</b> ({b.sender_role})</span>
                <span>{new Date(b.created_at).toLocaleString()}</span>
                <span><Users className="h-3 w-3 inline mr-0.5" />{b.recipient_count}</span>
                <span className="text-emerald-700"><Eye className="h-3 w-3 inline mr-0.5" />{b.read_count} read</span>
                {b.attachments?.length > 0 && <span><Paperclip className="h-3 w-3 inline mr-0.5" />{b.attachments.length}</span>}
                {b.voice_url && <span>🎙️ Voice</span>}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setOpenId(b.id)} data-testid={`view-bc-${b.id}`}>
              <MessageSquareText className="h-3.5 w-3.5 mr-1" />WhatsApp Links
            </Button>
          </div>
        </div>
      ))}

      {openId && <WhatsAppLinksDialog bid={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function WhatsAppLinksDialog({ bid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/broadcasts/${bid}/whatsapp-links`).then(r => setData(r.data));
  }, [bid]);

  const copy = (link) => {
    navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareText className="h-5 w-5 text-emerald-600" />
            WhatsApp Click-to-Send Links
          </DialogTitle>
        </DialogHeader>
        {!data && <p className="text-xs text-slate-400 p-6 text-center">Loading…</p>}
        {data && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="bg-slate-50 border rounded p-2 text-center"><div className="text-lg font-bold">{data.total}</div><div className="text-[10px] uppercase text-slate-500">Total</div></div>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center"><div className="text-lg font-bold text-emerald-700">{data.with_phone}</div><div className="text-[10px] uppercase text-emerald-700">With Phone</div></div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center"><div className="text-lg font-bold text-amber-700">{data.total - data.with_phone}</div><div className="text-[10px] uppercase text-amber-700">Missing Phone</div></div>
            </div>
            <div className="overflow-y-auto flex-1 border rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold">Employee</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Phone</th>
                    <th className="text-right px-2 py-1.5 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.rows.map(r => (
                    <tr key={r.employee_id} className={r.has_phone ? "" : "opacity-60"} data-testid={`wa-row-${r.employee_id}`}>
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{r.employee_name}</div>
                        <div className="text-[10px] text-slate-400">{r.employee_code}</div>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-[11px]">{r.phone_e164 || <span className="text-amber-600">No phone</span>}</td>
                      <td className="px-2 py-1.5 text-right">
                        {r.has_phone ? (
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => copy(r.link)} className="h-6 text-[10px]">Copy</Button>
                            <Button size="sm" onClick={() => window.open(r.link, "_blank")} className="h-6 text-[10px] brand-bg brand-bg-hover text-white" data-testid={`send-wa-${r.employee_id}`}>
                              <ExternalLink className="h-3 w-3 mr-0.5" />Open
                            </Button>
                          </div>
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
